import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { Plus, Building2, X, MapPin, Clock, CreditCard, Trash2, AlertTriangle, Shield } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow, getNeumorphicInset, getNeumorphicHover } from '@/lib/designSystem';

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [formData, setFormData] = useState({
    org_name: '',
    org_code: '',
    org_country: 'India',
    org_timezone: 'Asia/Kolkata',
    subscription_plan: 'FREE',
  });
  const { theme } = useTheme();
  const currentColors = colors[theme];

  const checkSuperAdmin = useCallback(() => {
    try {
      const userData = sessionStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setIsSuperAdmin(user.is_super_admin === true);
      }
    } catch (err) {
      console.error('Error checking super admin status:', err);
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response.data);
    } catch (err) {
      console.error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
    checkSuperAdmin();
  }, [loadOrganizations, checkSuperAdmin]);

  const handleDeleteOrganization = async (org) => {
    // Require typed confirmation
    if (deleteConfirmText !== 'DELETE_ORG') {
      setDeleteError('Please type DELETE_ORG to confirm');
      return;
    }
    
    setDeleting(true);
    setDeleteError('');
    try {
      await api.deleteOrganization(org.org_id);
      setShowDeleteConfirm(null);
      setDeleteConfirmText('');
      loadOrganizations();
    } catch (error) {
      console.error('Error deleting organization:', error);
      setDeleteError(error.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  // Reset delete form when modal closes
  const closeDeleteModal = () => {
    setShowDeleteConfirm(null);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createOrganization(formData);
      setShowForm(false);
      setFormData({
        org_name: '',
        org_code: '',
        org_country: 'India',
        org_timezone: 'Asia/Kolkata',
        subscription_plan: 'FREE',
      });
      loadOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Failed to create organization');
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'PREMIUM':
        return currentColors.accentPurple;
      case 'STANDARD':
        return currentColors.accentBlue;
      default:
        return currentColors.textSecondary;
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="organizations-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: currentColors.textPrimary }}>
            Organizations 🏢
          </h1>
          <p style={{ color: currentColors.textSecondary }}>
            Manage your organizations and their settings
          </p>
        </div>
        <motion.button
          onClick={() => setShowForm(true)}
          data-testid="create-organization-btn"
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white"
          style={{
            background: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})`,
            boxShadow: getNeumorphicShadow(theme),
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Plus size={20} />
          Create Organization
        </motion.button>
      </motion.div>

      {/* Organizations Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <motion.div
            className="w-16 h-16 rounded-full"
            style={{
              border: `4px solid ${currentColors.accentBlue}`,
              borderTopColor: 'transparent',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {organizations.map((org, index) => (
            <motion.div
              key={org.org_id}
              className="rounded-2xl p-6 relative"
              style={{
                background: currentColors.cardBg,
                boxShadow: getNeumorphicShadow(theme),
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{
                scale: 1.02,
                boxShadow: getNeumorphicHover(theme),
              }}
              data-testid={`org-card-${org.org_id}`}
            >
              {/* Delete Button - Super Admin Only */}
              {isSuperAdmin && (
                <div className="absolute top-4 right-4">
                  <motion.button
                    onClick={() => setShowDeleteConfirm(org)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{
                      background: currentColors.background,
                      boxShadow: getNeumorphicShadow(theme),
                    }}
                    whileHover={{ scale: 1.1, backgroundColor: '#fee2e2' }}
                    whileTap={{ scale: 0.9 }}
                    data-testid={`delete-org-btn-${org.org_id}`}
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </motion.button>
                  <div className="absolute -bottom-6 right-0 whitespace-nowrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex items-center gap-1">
                      <Shield size={8} />
                      SUPER_ADMIN
                    </span>
                  </div>
                </div>
              )}

              {/* Icon */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})`,
                }}
              >
                <Building2 size={32} className="text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold mb-2" style={{ color: currentColors.textPrimary }}>
                {org.org_name}
              </h3>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm" style={{ color: currentColors.textSecondary }}>
                  <MapPin size={16} />
                  {org.org_country}
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: currentColors.textSecondary }}>
                  <Clock size={16} />
                  {org.org_timezone}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard size={16} style={{ color: getPlanColor(org.subscription_plan) }} />
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: getPlanColor(org.subscription_plan) + '20',
                      color: getPlanColor(org.subscription_plan),
                    }}
                  >
                    {org.subscription_plan}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t" style={{ borderColor: currentColors.border }}>
                <p className="text-xs" style={{ color: currentColors.textSecondary }}>
                  Code: {org.org_code}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {organizations.length === 0 && !loading && (
        <motion.div
          className="text-center py-16 rounded-2xl"
          style={{
            background: currentColors.cardBg,
            boxShadow: getNeumorphicShadow(theme),
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Building2 className="mx-auto mb-4" size={48} style={{ color: currentColors.textSecondary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: currentColors.textPrimary }}>
            No organizations yet
          </h3>
          <p style={{ color: currentColors.textSecondary }}>
            Create your first organization to get started
          </p>
        </motion.div>
      )}

      {/* Create Form Modal */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowForm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="rounded-2xl p-6 w-full max-w-md"
                data-testid="organization-form"
                style={{
                  background: currentColors.cardBg,
                  boxShadow: getNeumorphicHover(theme),
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold" style={{ color: currentColors.textPrimary }}>
                    Create Organization
                  </h2>
                  <motion.button
                    onClick={() => setShowForm(false)}
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: currentColors.background,
                      boxShadow: getNeumorphicShadow(theme),
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={20} style={{ color: currentColors.textSecondary }} />
                  </motion.button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="org-name-input"
                      value={formData.org_name}
                      onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl focus:outline-none"
                      style={{
                        background: currentColors.background,
                        color: currentColors.textPrimary,
                        border: `1px solid ${currentColors.border}`,
                        boxShadow: getNeumorphicInset(theme),
                      }}
                      placeholder="Enter organization name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                      Organization Code *
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="org-code-input"
                      value={formData.org_code}
                      onChange={(e) => setFormData({ ...formData, org_code: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl focus:outline-none"
                      style={{
                        background: currentColors.background,
                        color: currentColors.textPrimary,
                        border: `1px solid ${currentColors.border}`,
                        boxShadow: getNeumorphicInset(theme),
                      }}
                      placeholder="e.g., ORG001"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                        Country
                      </label>
                      <input
                        type="text"
                        data-testid="org-country-input"
                        value={formData.org_country}
                        onChange={(e) => setFormData({ ...formData, org_country: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl focus:outline-none"
                        style={{
                          background: currentColors.background,
                          color: currentColors.textPrimary,
                          border: `1px solid ${currentColors.border}`,
                          boxShadow: getNeumorphicInset(theme),
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                        Plan
                      </label>
                      <select
                        data-testid="org-plan-select"
                        value={formData.subscription_plan}
                        onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl focus:outline-none"
                        style={{
                          background: currentColors.background,
                          color: currentColors.textPrimary,
                          border: `1px solid ${currentColors.border}`,
                          boxShadow: getNeumorphicInset(theme),
                        }}
                      >
                        <option value="FREE">Free</option>
                        <option value="STANDARD">Standard</option>
                        <option value="PREMIUM">Premium</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <motion.button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-6 py-3 rounded-xl font-medium"
                      style={{
                        background: currentColors.background,
                        color: currentColors.textSecondary,
                        boxShadow: getNeumorphicShadow(theme),
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      data-testid="submit-org-btn"
                      className="flex-1 px-6 py-3 rounded-xl font-medium text-white"
                      style={{
                        background: `linear-gradient(135deg, ${currentColors.accentBlue}, ${currentColors.accentPurple})`,
                        boxShadow: getNeumorphicShadow(theme),
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Create
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal - Super Admin Only */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => !deleting && setShowDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="rounded-2xl p-6 w-full max-w-md"
                data-testid="delete-org-modal"
                style={{
                  background: currentColors.cardBg,
                  boxShadow: getNeumorphicHover(theme),
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-100"
                  >
                    <AlertTriangle size={24} className="text-red-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: currentColors.textPrimary }}>
                      Delete Organization
                    </h2>
                    <p className="text-sm" style={{ color: currentColors.textSecondary }}>
                      This action cannot be undone
                    </p>
                  </div>
                </div>

                <div
                  className="p-4 rounded-xl mb-6"
                  style={{
                    background: currentColors.background,
                    boxShadow: getNeumorphicInset(theme),
                  }}
                >
                  <p className="text-sm mb-3" style={{ color: currentColors.textPrimary }}>
                    You are about to delete:
                  </p>
                  <p className="font-bold text-lg" style={{ color: currentColors.textPrimary }}>
                    {showDeleteConfirm.org_name}
                  </p>
                  <p className="text-xs mt-2" style={{ color: currentColors.textSecondary }}>
                    Code: {showDeleteConfirm.org_code}
                  </p>
                  
                  <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-xs text-red-600 font-medium">
                      ⚠️ This will permanently delete:
                    </p>
                    <ul className="text-xs text-red-600 mt-2 space-y-1">
                      <li>• All outlets in this organization</li>
                      <li>• All user-organization associations</li>
                      <li>• All ingredients and mappings</li>
                      <li>• All module configurations</li>
                    </ul>
                  </div>

                  {/* Typed Confirmation */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                      Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE_ORG</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl focus:outline-none font-mono"
                      style={{
                        background: currentColors.background,
                        color: currentColors.textPrimary,
                        border: deleteConfirmText === 'DELETE_ORG' 
                          ? '2px solid #22c55e' 
                          : `1px solid ${currentColors.border}`,
                        boxShadow: getNeumorphicInset(theme),
                      }}
                      placeholder="DELETE_ORG"
                      data-testid="delete-confirm-input"
                    />
                    {deleteError && (
                      <p className="text-red-500 text-sm mt-2">{deleteError}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    type="button"
                    onClick={closeDeleteModal}
                    disabled={deleting}
                    className="flex-1 px-6 py-3 rounded-xl font-medium"
                    style={{
                      background: currentColors.background,
                      color: currentColors.textSecondary,
                      boxShadow: getNeumorphicShadow(theme),
                      opacity: deleting ? 0.5 : 1,
                    }}
                    whileHover={{ scale: deleting ? 1 : 1.02 }}
                    whileTap={{ scale: deleting ? 1 : 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => handleDeleteOrganization(showDeleteConfirm)}
                    disabled={deleting || deleteConfirmText !== 'DELETE_ORG'}
                    data-testid="confirm-delete-org-btn"
                    className="flex-1 px-6 py-3 rounded-xl font-medium text-white flex items-center justify-center gap-2"
                    style={{
                      background: (deleting || deleteConfirmText !== 'DELETE_ORG') ? '#9ca3af' : '#ef4444',
                      boxShadow: getNeumorphicShadow(theme),
                      cursor: (deleting || deleteConfirmText !== 'DELETE_ORG') ? 'not-allowed' : 'pointer',
                    }}
                    whileHover={{ scale: (deleting || deleteConfirmText !== 'DELETE_ORG') ? 1 : 1.02 }}
                    whileTap={{ scale: (deleting || deleteConfirmText !== 'DELETE_ORG') ? 1 : 0.98 }}
                  >
                    {deleting ? (
                      <>
                        <motion.div
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete Organization
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Organizations;
